import { Link, useLocation } from 'react-router-dom'
import {
  BarChart3,
  MessageSquare,
  Users,
  TrendingUp,
  Activity,
  FileSearch,
  Settings,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useFeatureFlags } from '@/contexts/feature-flags-context'
import { useTheme } from '@/hooks/use-theme'

interface SidebarNavProps {
  isMobile?: boolean
  isOpen?: boolean
  onClose?: () => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}

// `labelKey` resolves to a nav.* catalog entry at render time so labels follow
// the active UI language.
const baseNavigation = [
  { labelKey: 'nav.dashboard', icon: BarChart3, path: '/dashboard' },
  { labelKey: 'nav.reviews', icon: MessageSquare, path: '/reviews' },
  { labelKey: 'nav.competitors', icon: Users, path: '/competitors' },
  { labelKey: 'nav.insights', icon: Activity, path: '/insights' },
  {
    labelKey: 'nav.trends',
    icon: TrendingUp,
    path: '/trends',
    featureFlag: 'trends_page' as const,
  },
  {
    labelKey: 'nav.adminSnapshots',
    icon: FileSearch,
    path: '/admin/snapshots',
    featureFlag: 'admin_tools' as const,
  },
  { labelKey: 'nav.settings', icon: Settings, path: '/settings' },
]

export function SidebarNav({
  isMobile = false,
  isOpen = false,
  onClose,
  collapsed = false,
  onToggleCollapse,
}: SidebarNavProps) {
  const location = useLocation()
  const { isEnabled } = useFeatureFlags()
  const { resolvedTheme, setTheme } = useTheme()
  const { t } = useTranslation()

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  const navigation = baseNavigation.filter(
    (item) => !item.featureFlag || isEnabled(item.featureFlag)
  )

  const sidebarContent = (
    <div
      className={cn(
        'relative flex h-full flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300 ease-in-out',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-16 shrink-0 items-center border-b border-sidebar-border px-3">
        <div
          className={cn(
            'flex items-center justify-center overflow-hidden transition-all duration-300',
            collapsed ? 'w-full' : 'w-full opacity-100'
          )}
        >
          {collapsed ? (
            <img src="/logo-icon.svg" alt="Savorias" className="h-8 w-8" />
          ) : (
            <img src="/logo-full.svg" alt="Savorias" className="h-auto w-full max-w-[200px]" />
          )}
        </div>
      </div>
      <nav className="flex flex-1 flex-col px-2 py-4">
        <ul role="list" className="flex flex-1 flex-col gap-y-1">
          <TooltipProvider delayDuration={0}>
            {navigation.map((item) => {
              const isCurrent = location.pathname === item.path
              const label = t(item.labelKey)
              const NavButton = (
                <li key={item.path} className="relative">
                  <Link
                    to={item.path}
                    onClick={() => isMobile && onClose?.()}
                    className={cn(
                      isCurrent
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
                      'group flex gap-x-3 rounded-lg p-3 transition-all w-full text-left items-center relative',
                      'pl-3',
                      collapsed && 'justify-center'
                    )}
                  >
                    <item.icon
                      className={cn(
                        isCurrent
                          ? 'text-sidebar-primary-foreground'
                          : 'text-muted-foreground group-hover:text-foreground',
                        'h-5 w-5 shrink-0 transition-colors'
                      )}
                      aria-hidden="true"
                    />
                    <span
                      className={cn(
                        'font-medium transition-all duration-300',
                        collapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'
                      )}
                    >
                      {label}
                    </span>
                  </Link>
                </li>
              )

              if (collapsed && !isMobile) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{NavButton}</TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {label}
                    </TooltipContent>
                  </Tooltip>
                )
              }

              return NavButton
            })}
          </TooltipProvider>
        </ul>
      </nav>

      {/* Theme toggle and collapse at bottom */}
      <div className="border-t border-sidebar-border p-2 space-y-1">
        {/* Dark mode toggle */}
        <button
          onClick={toggleTheme}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg p-2 text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground transition-colors',
            collapsed && !isMobile && 'justify-center'
          )}
          aria-label={resolvedTheme === 'dark' ? t('nav.switchToLight') : t('nav.switchToDark')}
        >
          {resolvedTheme === 'dark' ? (
            <>
              <Sun className="h-4 w-4" />
              <span
                className={cn(
                  'text-sm transition-all duration-300',
                  collapsed && !isMobile ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'
                )}
              >
                {t('nav.lightMode')}
              </span>
            </>
          ) : (
            <>
              <Moon className="h-4 w-4" />
              <span
                className={cn(
                  'text-sm transition-all duration-300',
                  collapsed && !isMobile ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'
                )}
              >
                {t('nav.darkMode')}
              </span>
            </>
          )}
        </button>

        {/* Collapse toggle */}
        {!isMobile && onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg p-2 text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground transition-colors',
              collapsed && 'justify-center'
            )}
            aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span className="text-sm">{t('nav.collapse')}</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )

  // Mobile version uses Sheet
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="left" className="p-0 w-64 lg:hidden" aria-describedby={undefined}>
          <SheetTitle className="sr-only">{t('nav.navigationMenu')}</SheetTitle>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop version
  return (
    <div
      className={cn(
        'flex h-full flex-col transition-all duration-300 ease-in-out',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {sidebarContent}
    </div>
  )
}
